import { useCanvas } from "@/hooks/useCanvas";
import { getParticipationVotes, pingParticipation, putParticipationVotes } from "@/services/participationApi";
import type { CanvasData } from "@/services/apitypes";
import { usePriorityStore, type SelectedTopic, type TopicId } from "@/stores/priorityStore";
import { getOrCreateAnonymousVoterId, isVotingEnabled } from "@canucci/base/canvas";
import { useAuth } from "@canucci/base/auth";
import { useEffect, useRef } from "react";
import {
  acknowledgeParticipationVotes,
  clearParticipationVoteSync,
  hasAcknowledgedParticipationVotes,
  participationVoteSyncKey,
  queueParticipationVoteAutosave,
  snapshotFromSelection,
} from "./participationVoteSync";

function selectedTopics(canvas: CanvasData, topicIds: TopicId[]): Record<TopicId, SelectedTopic> {
  const requested = new Set(topicIds);
  const result: Record<TopicId, SelectedTopic> = {};
  for (const [groupIndex, theme] of (canvas.themes ?? []).entries()) {
    const groupTitle = theme.text.title ?? `Group ${groupIndex + 1}`;
    for (const [areaIndex, subtheme] of (theme.subthemes ?? []).entries()) {
      if (!requested.has(subtheme.id)) continue;
      result[subtheme.id] = {
        topicId: subtheme.id,
        groupIndex,
        areaIndex,
        title: subtheme.text.title ?? `Topic ${areaIndex + 1}`,
        groupTitle,
        label: groupTitle,
      };
    }
  }
  return result;
}

export function VoteSync() {
  const { user } = useAuth();
  const {
    canvasResult: { data: canvas },
    isParticipation,
    participationToken,
  } = useCanvas();
  const selectedTopicIds = usePriorityStore((state) => state.selectedTopicIds);
  const comments = usePriorityStore((state) => state.comments);
  const hydrateFromServer = usePriorityStore((state) => state.hydrateFromServer);
  const canvasRef = useRef(canvas);
  canvasRef.current = canvas;
  const isSubthemeVoting = isVotingEnabled(canvas?.voting) && canvas?.voting?.target_type === "subtheme";
  const isAuthenticatedVoting = canvas?.voting?.access_mode === "authenticated";
  const voterId = participationToken && !isAuthenticatedVoting ? getOrCreateAnonymousVoterId(participationToken) : null;
  const identityKey = isAuthenticatedVoting ? user?.uid : voterId;
  const syncKey =
    participationToken && identityKey
      ? participationVoteSyncKey({
          token: participationToken,
          accessMode: isAuthenticatedVoting ? "authenticated" : "anonymous",
          identityKey,
        })
      : null;

  useEffect(() => {
    const activeSyncKey = syncKey;
    if (activeSyncKey) clearParticipationVoteSync(activeSyncKey);
    const currentCanvas = canvasRef.current;
    if (
      !currentCanvas ||
      !isParticipation ||
      !isSubthemeVoting ||
      !participationToken ||
      !activeSyncKey ||
      (isAuthenticatedVoting ? !user : !voterId)
    )
      return;
    let cancelled = false;
    void (async () => {
      const identity = isAuthenticatedVoting ? { authToken: await user!.getIdToken() } : { voterId: voterId! };
      const votes = await getParticipationVotes(participationToken, identity);
      if (currentCanvas.voting?.status === "live") {
        void pingParticipation(participationToken, identity).catch((error) =>
          console.error("Failed to update participation activity", error),
        );
      }
      return votes;
    })()
      .then((votes) => {
        if (cancelled) return;
        const ids = votes.map((vote) => vote.target_id);
        const voteComments = Object.fromEntries(
          votes.filter((vote) => vote.comment).map((vote) => [vote.target_id, vote.comment as string]),
        );
        acknowledgeParticipationVotes(activeSyncKey, { ids, comments: voteComments });
        hydrateFromServer({
          selectedTopicIds: ids,
          selectedTopics: selectedTopics(currentCanvas, ids),
          comments: voteComments,
        });
      })
      .catch((error) => console.error("Failed to initialize participation voting", error));
    return () => {
      cancelled = true;
    };
  }, [
    canvas?.resolved_language,
    canvas?.synthesis_id,
    canvas?.voting?.status,
    hydrateFromServer,
    isParticipation,
    isSubthemeVoting,
    isAuthenticatedVoting,
    participationToken,
    syncKey,
    voterId,
    user,
  ]);

  useEffect(() => {
    if (
      !isParticipation ||
      !isSubthemeVoting ||
      !participationToken ||
      !syncKey ||
      (isAuthenticatedVoting ? !user : !voterId) ||
      canvas?.voting?.status !== "live"
    ) {
      return;
    }
    if (!hasAcknowledgedParticipationVotes(syncKey)) return;
    const timer = globalThis.setTimeout(() => {
      const current = snapshotFromSelection(selectedTopicIds, comments);
      void queueParticipationVoteAutosave(syncKey, current, async (changes) => {
        if (changes.length === 0) return;
        const identity = isAuthenticatedVoting ? { authToken: await user!.getIdToken() } : { voterId: voterId! };
        await putParticipationVotes(participationToken, identity, changes);
      }).catch((error) => console.error("Failed to synchronize participation votes", error));
    }, 400);
    return () => globalThis.clearTimeout(timer);
  }, [
    canvas?.voting?.status,
    comments,
    isParticipation,
    isSubthemeVoting,
    isAuthenticatedVoting,
    participationToken,
    selectedTopicIds,
    syncKey,
    voterId,
    user,
  ]);

  return null;
}
